import type { PulseChart } from '@shared/chart';
import type { EngineState } from '@shared/engine/pulse/state';
import type { FlashSpec, FloatingText } from './pulseRenderTypes';

/**
 * Stateless overlay drawers: full-viewport flashes, floating texts that
 * fade upward, the GO/3/2/1 countdown, and the first-run TAP SPACE hint.
 * Each function consumes its input array in-place (texts/flashes) and
 * advances time via the `dt` argument.
 */

export function drawFlashes(
  ctx: CanvasRenderingContext2D,
  flashes: FlashSpec[],
  w: number,
  h: number,
  dt: number,
): void {
  for (let i = flashes.length - 1; i >= 0; i--) {
    const f = flashes[i];
    f.t -= dt;
    if (f.t <= 0) {
      flashes.splice(i, 1);
      continue;
    }
    const intensity = f.intensity ?? 1;
    const a = f.t * 1.5 * intensity;
    ctx.save();
    ctx.globalAlpha = Math.min(1, a);
    const colour =
      f.type === 'milestone'
        ? f.colour ?? '#ffd84d'
        : f.type === 'perfect'
          ? '#3bff9d'
          : f.type === 'good'
            ? '#16e0ff'
            : f.type === 'miss'
              ? '#ff3b5c'
              : f.type === 'flip'
                ? '#ff2bd6'
                : f.type === 'drop'
                  ? '#ffffff'
                  : '#ff3b5c';
    ctx.fillStyle = colour;
    if (f.type === 'drop') {
      ctx.fillRect(0, 0, w, h);
    } else if (f.type === 'milestone') {
      // Full-edge halo + a soft radial glow centred on the player.
      // Heavier than a regular hit flash because the celebration is
      // earned, but still time-limited so the next pad reads cleanly.
      const thickness = Math.min(28 * intensity, f.t * 90);
      ctx.fillRect(0, 0, w, thickness);
      ctx.fillRect(0, h - thickness, w, thickness);
      ctx.fillRect(0, 0, thickness, h);
      ctx.fillRect(w - thickness, 0, thickness, h);
    } else {
      const thickness = Math.min(10 * intensity, f.t * 60);
      ctx.fillRect(0, 0, w, thickness);
      ctx.fillRect(0, h - thickness, w, thickness);
    }
    ctx.restore();
  }
}

export function drawFloatingTexts(
  ctx: CanvasRenderingContext2D,
  texts: FloatingText[],
  dt: number,
): void {
  for (let i = texts.length - 1; i >= 0; i--) {
    const t = texts[i];
    t.life -= dt;
    t.y += t.vy * dt;
    t.vy *= 0.97;
    if (t.life <= 0) {
      texts.splice(i, 1);
      continue;
    }
    const progress = 1 - t.life / t.maxLife;
    const alpha = Math.min(1, t.life / 0.25);
    const scale = t.scale * (1 + (1 - alpha) * 0.3) * (t.big ? 1 + Math.sin(progress * 4) * 0.05 : 1);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(t.x, t.y);
    ctx.scale(scale, scale);
    ctx.shadowColor = t.colour;
    ctx.shadowBlur = t.big ? 28 : 14;
    ctx.fillStyle = t.colour;
    ctx.font = `bold ${t.big ? 36 : 22}px 'Rajdhani', 'Orbitron', system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(t.text, 0, 0);
    ctx.restore();
  }
}

export function drawCountdown(
  ctx: CanvasRenderingContext2D,
  countdown: number | null,
  w: number,
  h: number,
): void {
  if (countdown === null) {
    return;
  }
  const phase = countdown - Math.floor(countdown);
  const num = Math.ceil(countdown);
  const label = num > 0 ? String(num) : 'GO!';
  const scale = 1 + (1 - phase) * 0.6;
  ctx.save();
  ctx.globalAlpha = 0.4 + phase * 0.6;
  ctx.translate(w / 2, h / 2);
  ctx.scale(scale, scale);
  ctx.shadowColor = label === 'GO!' ? '#3bff9d' : '#ff2bd6';
  ctx.shadowBlur = 40;
  ctx.fillStyle = '#fff';
  ctx.font = `bold 96px 'Rajdhani', 'Orbitron', system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, 0, 0);
  ctx.restore();
}

/**
 * First-time-player onboarding hint: a pulsing "TAP SPACE" badge above
 * the next un-hit required pad with an arrow pointing at it. Disappears
 * after the player hits their first pad.
 */
export function drawTapHint(
  ctx: CanvasRenderingContext2D,
  chart: PulseChart,
  state: EngineState,
  beat: number,
  playerX: number,
  ceilingY: number,
  floorY: number,
  xPerBeat: number,
  viewWidth: number,
): void {
  // Find the earliest required pad ahead of the player that hasn't been
  // hit yet. We sort to be safe; chart.pads is conventionally sorted by
  // beat but this isn't a schema guarantee (user-built maps could
  // reorder).
  let target: { beat: number; surface: 'floor' | 'ceiling' } | null = null;
  let bestBeat = Infinity;
  for (const pad of chart.pads) {
    if (pad.optional) {
      continue;
    }
    if (state.hitPadIds.has(pad.id)) {
      continue;
    }
    if (pad.beat < beat - 0.1) {
      continue;
    }
    if (pad.beat < bestBeat) {
      bestBeat = pad.beat;
      target = { beat: pad.beat, surface: pad.surface };
    }
  }
  if (!target) {
    return;
  }

  // Future pads sit to the RIGHT of playerX and scroll left.
  const padXRaw = playerX + (target.beat - beat) * xPerBeat;
  // Skip the hint once the pad has scrolled past the player to the left
  // (player has missed the window). 8px slack covers the perfect tolerance.
  if (padXRaw < playerX - 8) {
    return;
  }
  const isMobile = window.matchMedia('(pointer: coarse)').matches;
  const labelW = isMobile ? 70 : 130;
  // If the pad is off-screen to the RIGHT (still far in the future),
  // anchor the hint to the right edge so the badge stays visible. Once
  // the pad scrolls onto the canvas, the hint snaps to its true position.
  const rightAnchorX = viewWidth - labelW / 2 - 12;
  const isAnchored = padXRaw > rightAnchorX;
  const padX = isAnchored ? rightAnchorX : padXRaw;
  const targetY = target.surface === 'floor' ? floorY : ceilingY;

  const phase = (beat % 1 + 1) % 1;
  const pulse = 0.55 + 0.45 * Math.sin(phase * Math.PI * 2);

  ctx.save();
  const label = isMobile ? 'TAP' : 'TAP SPACE';
  const labelH = 30;
  const labelOffset = 50;
  const labelY = target.surface === 'floor' ? targetY - labelOffset - labelH : targetY + labelOffset;

  ctx.globalAlpha = 0.85;
  ctx.fillStyle = '#ff2bd6';
  ctx.shadowColor = '#ff2bd6';
  ctx.shadowBlur = 16 + pulse * 14;
  const radius = labelH / 2;
  ctx.beginPath();
  ctx.moveTo(padX - labelW / 2 + radius, labelY);
  ctx.lineTo(padX + labelW / 2 - radius, labelY);
  ctx.arcTo(padX + labelW / 2, labelY, padX + labelW / 2, labelY + labelH, radius);
  ctx.lineTo(padX + labelW / 2, labelY + labelH - radius);
  ctx.arcTo(
    padX + labelW / 2,
    labelY + labelH,
    padX + labelW / 2 - radius,
    labelY + labelH,
    radius,
  );
  ctx.lineTo(padX - labelW / 2 + radius, labelY + labelH);
  ctx.arcTo(padX - labelW / 2, labelY + labelH, padX - labelW / 2, labelY + labelH - radius, radius);
  ctx.lineTo(padX - labelW / 2, labelY + radius);
  ctx.arcTo(padX - labelW / 2, labelY, padX - labelW / 2 + radius, labelY, radius);
  ctx.closePath();
  ctx.fill();

  ctx.shadowBlur = 0;
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#fff';
  ctx.font = `bold 14px 'Rajdhani', 'Orbitron', system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, padX, labelY + labelH / 2);

  if (!isAnchored) {
    ctx.globalAlpha = 0.85 + pulse * 0.15;
    ctx.shadowColor = '#ff2bd6';
    ctx.shadowBlur = 12;
    ctx.fillStyle = '#ff2bd6';
    const arrowDir = target.surface === 'floor' ? 1 : -1;
    const arrowStart = target.surface === 'floor' ? labelY + labelH : labelY;
    const arrowEnd = targetY + arrowDir * 8;
    const arrowMidY = (arrowStart + arrowEnd) / 2;
    ctx.fillRect(
      padX - 1.5,
      Math.min(arrowStart, arrowMidY),
      3,
      Math.abs(arrowMidY - arrowStart),
    );
    ctx.beginPath();
    ctx.moveTo(padX, arrowEnd);
    ctx.lineTo(padX - 8, arrowEnd - arrowDir * 12);
    ctx.lineTo(padX + 8, arrowEnd - arrowDir * 12);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}
